/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth }   from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useToast }  from '../context/ToastContext';
import { usersAPI }  from '../utils/api';
import { getAvatarUrl, formatRelativeTime } from '../utils/helpers';
import {
  Phone, Video, PhoneMissed, PhoneIncoming,
  PhoneOff, Mic, MicOff, Camera, CameraOff,
  Search, Clock, Volume2, VolumeX, X
} from 'lucide-react';
import { getSocket } from '../utils/socket';
import './CallsPage.css';

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

// PRIVACY: only shows call history + explicit search results. Never "all users".

export default function CallsPage() {
  const { user }    = useAuth();
  const { isOnline } = useSocket();
  const toast       = useToast();

  const [search,      setSearch]      = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching,   setSearching]   = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [activeCall,  setActiveCall]  = useState(null); // {type,userId,userName,userAvatar,status}
  const [callTimer,   setCallTimer]   = useState(0);
  const [muted,       setMuted]       = useState(false);
  const [camOff,      setCamOff]      = useState(false);
  const [speaker,     setSpeaker]     = useState(true);
  const [incomingCall,setIncomingCall] = useState(null);
  const [hasLocalVideo,  setHasLocalVideo]  = useState(false);
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);

  const timerRef      = useRef(null);
  const localVideo    = useRef(null);
  const remoteVideo   = useRef(null);
  const remoteAudio   = useRef(null);
  const pcRef         = useRef(null);
  const streamRef     = useRef(null);
  const searchTimer   = useRef(null);
  const pendingIce    = useRef([]); // ICE candidates that arrive before remote description is set
  const myId = user?._id || user?.id;

  useEffect(() => {
    try { setCallHistory(JSON.parse(localStorage.getItem(`nexa_calls_${myId}`) || '[]')); } catch {}
  }, []);

  const saveHistory = (h) => {
    localStorage.setItem(`nexa_calls_${myId}`, JSON.stringify(h));
    setCallHistory(h);
  };

  // Search for someone to call
  useEffect(() => {
    clearTimeout(searchTimer.current);
    if (!search.trim() || search.trim().length < 2) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try { const { data } = await usersAPI.search(search.trim()); setSearchResults(Array.isArray(data)?data:[]); }
      catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const knownFromHistory = React.useMemo(() => {
    const map = new Map();
    callHistory.forEach(c => { if (!map.has(c.userId)) map.set(c.userId, { _id:c.userId, displayName:c.userName, avatar:c.userAvatar }); });
    return [...map.values()];
  }, [callHistory]);

  // ── WebRTC: build a fresh peer connection ─────────────────────────
  const createPeerConnection = (remoteUserId) => {
    const pc = new RTCPeerConnection(ICE_SERVERS);

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        const socket = getSocket();
        socket?.emit('call:ice', { to: remoteUserId, candidate: e.candidate });
      }
    };

    pc.ontrack = (e) => {
      const [stream] = e.streams;
      const videoTracks = stream?.getVideoTracks?.() || [];
      setHasRemoteVideo(videoTracks.length > 0 && videoTracks[0].enabled);
      if (remoteVideo.current) remoteVideo.current.srcObject = stream;
      if (remoteAudio.current) remoteAudio.current.srcObject = stream;
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        setActiveCall(a => a ? { ...a, status:'active' } : a);
      }
      if (['disconnected','failed','closed'].includes(pc.connectionState)) {
        // Let explicit end/hangup handlers manage cleanup — avoid double-closing
      }
    };

    return pc;
  };


  // Get camera+mic, but gracefully fall back to audio-only if the camera
  // is unavailable/blocked — many laptops/test machines have no webcam,
  // and previously a video-call failure would silently abort with no
  // notice to the other person (they'd just see "No answer").
  const getMediaStream = async (type) => {
    if (type === 'video') {
      try {
        return { stream: await navigator.mediaDevices.getUserMedia({ video:true, audio:true }), gotVideo:true };
      } catch(err) {
        console.warn('Camera unavailable, falling back to audio-only:', err.message);
        toast.info('Camera unavailable — joining with audio only');
        const stream = await navigator.mediaDevices.getUserMedia({ audio:true });
        return { stream, gotVideo:false };
      }
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio:true });
    return { stream, gotVideo:false };
  };

  const flushPendingIce = async () => {
    if (!pcRef.current) return;
    for (const cand of pendingIce.current) {
      try { await pcRef.current.addIceCandidate(new RTCIceCandidate(cand)); } catch {}
    }
    pendingIce.current = [];
  };

  // ── Socket listeners ────────────────────────────────────────────
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const onIncoming = (data) => {
      setIncomingCall({
        type: data.callType, userId: data.from,
        userName: data.callerInfo?.displayName || 'Unknown',
        userAvatar: data.callerInfo?.avatar || '',
        offer: data.offer, socketId: data.socketId,
      });
      toast.info(`📞 Incoming ${data.callType} call from ${data.callerInfo?.displayName||'Unknown'}`);
    };

    const onAnswered = async (data) => {
      try {
        if (pcRef.current && data.answer) {
          await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.answer));
          await flushPendingIce();
        }
      } catch(e) { console.error('setRemoteDescription (answer) failed', e); }
      setActiveCall(a => a ? { ...a, status:'active' } : a);
    };

    const onIce = async (data) => {
      if (!data.candidate) return;
      if (pcRef.current && pcRef.current.remoteDescription) {
        try { await pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch {}
      } else {
        pendingIce.current.push(data.candidate);
      }
    };

    const onRejected = (data) => {
      if (data?.reason === 'media_error') toast.error(`${activeCall?.userName||'They'} couldn't answer (mic/camera issue)`);
      else toast.info('Call declined');
      hangUp('declined');
    };
    const onEnded    = () => { hangUp('ended'); };
    const onBusy     = () => { toast.error('User is busy or offline'); hangUp('missed'); };

    socket.on('call:incoming', onIncoming);
    socket.on('call:answered', onAnswered);
    socket.on('call:ice',      onIce);
    socket.on('call:rejected', onRejected);
    socket.on('call:ended',    onEnded);
    socket.on('call:busy',     onBusy);

    return () => {
      socket.off('call:incoming', onIncoming);
      socket.off('call:answered', onAnswered);
      socket.off('call:ice',      onIce);
      socket.off('call:rejected', onRejected);
      socket.off('call:ended',    onEnded);
      socket.off('call:busy',     onBusy);
    };
  }, []);

  // Call timer
  useEffect(() => {
    if (activeCall?.status==='active') {
      timerRef.current = setInterval(() => setCallTimer(t=>t+1), 1000);
    } else {
      clearInterval(timerRef.current);
      setCallTimer(0);
    }
    return () => clearInterval(timerRef.current);
  }, [activeCall?.status]);

  const formatTimer = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  // ── Start an outgoing call ──────────────────────────────────────
  const startCall = async (contact, type='voice') => {
    const contactId = contact._id||contact.id;
    if (!isOnline(contactId)) { toast.error(`${contact.displayName} is offline`); return; }

    setActiveCall({ type, userId:contactId, userName:contact.displayName, userAvatar:contact.avatar||'', status:'calling' });
    setSearch(''); setSearchResults([]);

    try {
      const { stream, gotVideo } = await getMediaStream(type);
      streamRef.current = stream;
      setHasLocalVideo(gotVideo);
      if (gotVideo && localVideo.current) localVideo.current.srcObject = stream;

      const pc = createPeerConnection(contactId);
      pcRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const socket = getSocket();
      socket?.emit('call:offer', {
        to: contactId, callType: type, callerId: myId,
        callerInfo: { displayName:user?.displayName, avatar:user?.avatar },
        offer,
      });
    } catch(err) {
      console.error('startCall failed', err);
      toast.error('Could not access microphone. Please allow permission and try again.');
      hangUp('ended');
      return;
    }

    const entry = { id:`call_${Date.now()}`, type, userId:contactId, userName:contact.displayName, userAvatar:contact.avatar, status:'outgoing', time:new Date().toISOString() };
    saveHistory([entry, ...callHistory].slice(0,50));

    setTimeout(() => {
      setActiveCall(a => { if(a?.status==='calling') { toast.error('No answer'); hangUp('missed'); return null; } return a; });
    }, 30000);
  };

  // ── Answer an incoming call ─────────────────────────────────────
  const answerCall = async () => {
    console.log('[Nexa Calls] Accept clicked', incomingCall);
    if (!incomingCall) { console.warn('[Nexa Calls] answerCall called but incomingCall is null'); return; }
    const incoming = incomingCall;

    // Instant feedback — dismiss the popup and show "Connecting" right away,
    // BEFORE touching the microphone. If mic access hangs or is blocked,
    // the user still sees something happened instead of a frozen button.
    setIncomingCall(null);
    setActiveCall({ type:incoming.type, userId:incoming.userId, userName:incoming.userName, userAvatar:incoming.userAvatar, status:'connecting' });
    toast.info('Connecting…');

    const rejectAndBail = (msg) => {
      toast.error(msg);
      const socket = getSocket();
      socket?.emit('call:reject', { to: incoming.userId, reason:'media_error' });
      hangUp('ended');
    };

    // Explicit permission pre-check where supported — surfaces a clear
    // message instead of a silently-hanging getUserMedia() call.
    try {
      if (navigator.permissions?.query) {
        const micStatus = await navigator.permissions.query({ name:'microphone' }).catch(() => null);
        if (micStatus?.state === 'denied') {
          rejectAndBail('Microphone is blocked for this site. Click the 🔒/ⓘ icon next to the address bar → Site settings → allow Microphone, then try again.');
          return;
        }
      }
    } catch { /* permissions API not supported everywhere — ignore and proceed */ }

    // Hard timeout — getUserMedia must never be allowed to hang forever.
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      rejectAndBail('Timed out waiting for microphone access. Check your browser permission prompt (it may be hidden near the address bar).');
    }, 12000);

    try {
      const { stream, gotVideo } = await getMediaStream(incoming.type);
      clearTimeout(timeoutId);
      if (timedOut) { stream.getTracks().forEach(t=>t.stop()); return; }

      streamRef.current = stream;
      setHasLocalVideo(gotVideo);
      if (gotVideo && localVideo.current) localVideo.current.srcObject = stream;

      const pc = createPeerConnection(incoming.userId);
      pcRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));

      if (incoming.offer) {
        await pc.setRemoteDescription(new RTCSessionDescription(incoming.offer));
        await flushPendingIce();
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        const socket = getSocket();
        socket?.emit('call:answer', { to: incoming.userId, answer });
      } else {
        console.warn('[Nexa Calls] No offer received with incoming call — caller will time out.');
      }

      setActiveCall(a => a ? { ...a, status:'active' } : a);
    } catch(err) {
      clearTimeout(timeoutId);
      console.error('[Nexa Calls] answerCall failed:', err.name, err.message);
      if (err.name === 'NotAllowedError') {
        rejectAndBail('Microphone permission denied. Allow it in your browser and try again.');
      } else if (err.name === 'NotFoundError') {
        rejectAndBail('No microphone found on this device.');
      } else {
        rejectAndBail('Could not connect the call: ' + err.message);
      }
      return;
    }

    const entry = { id:`call_${Date.now()}`, type:incoming.type, userId:incoming.userId, userName:incoming.userName, userAvatar:incoming.userAvatar, status:'incoming', time:new Date().toISOString() };
    saveHistory([entry, ...callHistory].slice(0,50));
  };

  const rejectCall = () => {
    if (!incomingCall) return;
    const socket = getSocket();
    socket?.emit('call:reject', { to:incomingCall.userId });
    setIncomingCall(null);
    toast.info('Call declined');
  };

  // ── Cleanup / hang up ────────────────────────────────────────────
  const hangUp = (status='ended') => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t=>t.stop()); streamRef.current=null; }
    if (pcRef.current) { try { pcRef.current.close(); } catch {} pcRef.current=null; }
    pendingIce.current = [];
    setActiveCall(null); setMuted(false); setCamOff(false); setCallTimer(0);
    setHasLocalVideo(false); setHasRemoteVideo(false);
    if (status!=='ended') toast.info('Call ended');
  };

  const endCall = () => {
    if (activeCall) {
      const socket = getSocket();
      socket?.emit('call:end', { to: activeCall.userId });
    }
    hangUp('ended');
  };

  const toggleMic = () => { setMuted(m => { if (streamRef.current) streamRef.current.getAudioTracks().forEach(t=>t.enabled=m); return !m; }); };
  const toggleCam = () => { setCamOff(c => { if (streamRef.current) streamRef.current.getVideoTracks().forEach(t=>t.enabled=c); return !c; }); };

  const isSearchMode = search.trim().length >= 2;
  const displayList  = isSearchMode ? searchResults : knownFromHistory;

  const HISTORY_ICONS = { outgoing:<Phone size={14} style={{color:'var(--green)'}}/>, incoming:<PhoneIncoming size={14} style={{color:'var(--blue)'}}/>, missed:<PhoneMissed size={14} style={{color:'var(--red)'}}/> };

  return (
    <div className="calls-page">

      {/* ── Left: search / recent people ── */}
      <div className="calls-sidebar">
        <div className="calls-sidebar-header">
          <h2>Calls</h2>
          <p style={{fontSize:'.78rem',color:'var(--text-400)',marginTop:2}}>Start or view call history</p>
        </div>

        <div className="calls-search">
          <Search size={15}/>
          <input placeholder="Search people to call..." value={search} onChange={e=>setSearch(e.target.value)}/>
          {search && <button className="btn-icon" style={{width:22,height:22}} onClick={()=>setSearch('')}><X size={12}/></button>}
        </div>

        <div className="calls-list">
          {searching ? (
            <div style={{padding:'32px 16px',textAlign:'center'}}><div className="loader" style={{width:20,height:20,borderWidth:2,margin:'0 auto'}}/></div>
          ) : displayList.length===0 ? (
            <div style={{padding:'32px 16px',textAlign:'center',color:'var(--text-500)'}}>
              <Phone size={32} style={{opacity:.3,margin:'0 auto 10px'}}/>
              {isSearchMode ? <p style={{fontSize:'.85rem'}}>No users found</p> : (
                <>
                  <p style={{fontSize:'.85rem',fontWeight:500}}>No recent calls</p>
                  <p style={{fontSize:'.78rem',marginTop:6,lineHeight:1.5}}>Search above to find someone<br/>to call</p>
                </>
              )}
            </div>
          ) : displayList.map(c => {
            const cId    = c._id||c.id;
            const online = isOnline(cId);
            return (
              <div key={cId} className="calls-contact-item">
                <div style={{position:'relative',flexShrink:0}}>
                  <img src={getAvatarUrl(c)} alt="" className="avatar" style={{width:44,height:44}}/>
                  <span className={`status-dot ${online?'online':'offline'}`} style={{position:'absolute',bottom:0,right:0}}/>
                </div>
                <div className="calls-contact-info">
                  <p className="calls-contact-name">{c.displayName}</p>
                  <p className="calls-contact-sub">{isSearchMode ? `@${c.username}` : (online?'Online':'Offline')}</p>
                </div>
                <div className="calls-contact-actions">
                  <button className="calls-action-btn voice" title="Voice call" onClick={()=>startCall(c,'voice')} disabled={!!activeCall}><Phone size={16}/></button>
                  <button className="calls-action-btn video" title="Video call" onClick={()=>startCall(c,'video')} disabled={!!activeCall}><Video size={16}/></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Right: history / active call ── */}
      <div className="calls-main">
        {activeCall ? (
          <div className="call-screen">
            <div className="call-screen-bg"/>
            <div className="call-info">
              <div className="call-avatar-ring">
                <img src={getAvatarUrl({avatar:activeCall.userAvatar})} alt="" className="call-avatar"/>
                {activeCall.status==='active' && <div className="call-ring-anim"/>}
              </div>
              <h2 className="call-name">{activeCall.userName}</h2>
              <p className="call-status-text">
                {activeCall.status==='calling' && <><span className="call-dot"/><span style={{color:'var(--yellow)'}}>Calling…</span></>}
                {activeCall.status==='connecting' && <><span className="call-dot"/><span style={{color:'var(--yellow)'}}>Connecting…</span></>}
                {activeCall.status==='active' && <><span className="call-dot active"/><span style={{color:'var(--green)'}}>{formatTimer(callTimer)}</span></>}
              </p>
              <p style={{fontSize:'.8rem',color:'rgba(255,255,255,.5)',marginTop:4}}>
                {activeCall.type==='video'?'📹 Video call':'📞 Voice call'} • End-to-end encrypted
              </p>
            </div>

            {activeCall.type==='video' && (
              <div className="call-video-wrap">
                {/* Always mount the video elements (refs must exist before tracks arrive),
                    but visually hide them until there's an actual video track to show —
                    prevents an ugly black box when the other side has no camera. */}
                <video ref={remoteVideo} autoPlay playsInline muted={false}
                  className="call-remote-video" style={{ opacity: hasRemoteVideo ? 1 : 0 }}/>
                <video ref={localVideo}  autoPlay playsInline muted
                  className="call-local-video" style={{ display: hasLocalVideo ? 'block' : 'none' }}/>
                {!hasRemoteVideo && (
                  <p className="call-camera-off-hint">
                    📷 {activeCall.userName}'s camera is off
                  </p>
                )}
              </div>
            )}
            <audio ref={remoteAudio} autoPlay style={{display:'none'}}/>

            <div className="call-controls">
              <button className={`call-ctrl-btn ${muted?'active':''}`} onClick={toggleMic}>
                {muted ? <MicOff size={22}/> : <Mic size={22}/>}<span>{muted?'Unmute':'Mute'}</span>
              </button>
              {activeCall.type==='video' && (
                <button className={`call-ctrl-btn ${camOff?'active':''}`} onClick={toggleCam}>
                  {camOff ? <CameraOff size={22}/> : <Camera size={22}/>}<span>{camOff?'Cam on':'Cam off'}</span>
                </button>
              )}
              <button className={`call-ctrl-btn ${!speaker?'active':''}`} onClick={()=>setSpeaker(s=>!s)}>
                {speaker ? <Volume2 size={22}/> : <VolumeX size={22}/>}<span>Speaker</span>
              </button>
              <button className="call-ctrl-btn end" onClick={endCall}>
                <PhoneOff size={22}/><span>End</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="calls-history">
            <div className="calls-history-header"><Clock size={16} style={{color:'var(--primary-light)'}}/> Recent Calls</div>
            {callHistory.length===0 ? (
              <div className="empty-state">
                <Phone size={48}/>
                <h3>No call history</h3>
                <p>Search for someone on the left and tap 📞 or 📹 to start your first call</p>
              </div>
            ) : callHistory.map(call => (
              <div key={call.id} className="calls-history-item">
                <img src={getAvatarUrl({avatar:call.userAvatar})} alt="" className="avatar" style={{width:44,height:44,flexShrink:0}}/>
                <div className="calls-history-info">
                  <div style={{display:'flex',alignItems:'center',gap:6}}>{HISTORY_ICONS[call.status]}<p className="calls-history-name">{call.userName}</p></div>
                  <p className="calls-history-meta">{call.type==='video'?'📹':'📞'} {call.status} • {formatRelativeTime(call.time)}</p>
                </div>
                <div className="calls-history-actions">
                  <button className="calls-action-btn voice" title="Call back" disabled={!!activeCall}
                    onClick={()=>startCall({_id:call.userId,displayName:call.userName,avatar:call.userAvatar}, call.type)}>
                    <Phone size={15}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {incomingCall && (
        <div className="incoming-call-overlay">
          <div className="incoming-call-card">
            <div className="incoming-call-pulse"/>
            <img src={getAvatarUrl({avatar:incomingCall.userAvatar})} alt="" style={{width:80,height:80,borderRadius:'50%',border:'3px solid var(--primary)',margin:'0 auto 14px',display:'block'}}/>
            <h3 style={{textAlign:'center',marginBottom:4}}>{incomingCall.userName}</h3>
            <p style={{textAlign:'center',color:'var(--text-400)',fontSize:'.85rem',marginBottom:28}}>Incoming {incomingCall.type} call…</p>
            <div style={{display:'flex',gap:20,justifyContent:'center'}}>
              <button className="incoming-reject-btn" onClick={rejectCall}><PhoneOff size={24}/><span>Decline</span></button>
              <button className="incoming-accept-btn" onClick={answerCall}><Phone size={24}/><span>Accept</span></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}